import axios from 'axios';

const ACCESS_TOKEN_KEY = 'mcg_access_token';
const REFRESH_TOKEN_KEY = 'mcg_refresh_token';

export const getAccessToken = () => sessionStorage.getItem(ACCESS_TOKEN_KEY);
export const getRefreshToken = () => sessionStorage.getItem(REFRESH_TOKEN_KEY);
export const setTokens = ({ accessToken, refreshToken }) => {
  if (accessToken) sessionStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  if (refreshToken) sessionStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};
export const clearTokens = () => {
  sessionStorage.removeItem(ACCESS_TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_TOKEN_KEY);
};

export function decodeAccessToken() {
  const token = getAccessToken();
  if (!token) return null;
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(atob(normalized).split('').map((c) => `%${c.charCodeAt(0).toString(16).padStart(2, '0')}`).join(''));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function hasUsableAccessToken() {
  const token = getAccessToken();
  if (!token) return false;
  const claims = decodeAccessToken();
  if (claims?.exp && Date.now() >= claims.exp * 1000) {
    clearTokens();
    return false;
  }
  return true;
}

const baseURL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

export const apiClient = axios.create({
  baseURL,
  timeout: Number(import.meta.env.VITE_API_TIMEOUT_MS || 15000),
  headers: { 'Content-Type': 'application/json' }
});

let refreshPromise = null;

function publishError(err) {
  const data = err?.response?.data;
  const payload = {
    status: err?.response?.status || 0,
    code: data?.code || `CG-NET-${err?.response?.status || 'UNKNOWN'}`,
    message: data?.message || err?.message || 'Request failed.',
    fieldErrors: data?.fieldErrors || []
  };
  window.dispatchEvent(new CustomEvent('mcg:api-error', { detail: payload }));
}

async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error('No refresh token');

  if (!refreshPromise) {
    refreshPromise = axios.post(`${baseURL}/auth/refresh`, { refreshToken }, {
      timeout: 10000,
      headers: { 'Content-Type': 'application/json' }
    }).then(({ data }) => {
      setTokens(data);
      return data.accessToken;
    }).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

apiClient.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (!config.headers['X-Correlation-Id']) {
    config.headers['X-Correlation-Id'] = crypto.randomUUID();
  }
  return config;
});

apiClient.interceptors.response.use(
  (res) => res,
  async (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || '';
    const isAuthRequest = /\/auth\/(login|refresh|mfa\/verify)/.test(url);

    if (status === 401 && !err.config?._retry && !isAuthRequest && getRefreshToken()) {
      err.config._retry = true;
      try {
        const token = await refreshAccessToken();
        err.config.headers.Authorization = `Bearer ${token}`;
        return apiClient(err.config);
      } catch {
        clearTokens();
        window.dispatchEvent(new CustomEvent('mcg:auth-expired'));
      }
    }

    publishError(err);
    return Promise.reject(err);
  }
);

export default apiClient;
